import { H4 } from "@/shared/components/typography";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { Button } from "@/shared/components/ui/button";
import t from "@/lang/lang";
import {
  useRequirementData,
  useRequirementEdit,
} from "@/services/backend/qpm/requirements/hooks/requirement-context";
import { RequirementDTO } from "@/services/backend/qpm/requirements/types/requirement-dto";
import type { PestSelectionDTO } from "@/services/backend/qpm/requirements/types/pest-selection";
import type { PestSnapDTO } from "@/services/backend/qpm/monitors/types/monitor-snapshot";
import { PestKind } from "@/services/backend/qpm/pests/types/pest-enum";

type PestComponentProps = {
  requirement: RequirementDTO;
};

export function PestsComponent({ requirement }: PestComponentProps) {
  const { updateRequirement, putRequest } = useRequirementEdit();
  const { requirement: reqData } = useRequirementData();

  const { pestSnaps } = requirement;

  const selectedPestsRecord =
    putRequest.requirement.selectedPests ??
    reqData?.selectedPests ??
    requirement.selectedPests;

  const pestOptions: PestSnapDTO[] = Object.values(pestSnaps ?? {}).slice();

  const getDisplayLabel = (snap: PestSnapDTO) => {
    const maybeAbbrev = (snap as unknown as { abbreviation?: string })
      .abbreviation;

    return snap.kind === PestKind.Indicator && maybeAbbrev
      ? maybeAbbrev
      : snap.name;
  };

  const sortedOptions = pestOptions.sort((a, b) => {
    const kindCmp = (a.kind ?? "").localeCompare(b.kind ?? "");
    if (kindCmp !== 0) return kindCmp;
    return getDisplayLabel(a).localeCompare(getDisplayLabel(b));
  });

  const pestsByKind = sortedOptions.reduce<Record<string, PestSnapDTO[]>>(
    (acc, snap) => {
      const key = snap.kind ?? "unknown";
      acc[key] = acc[key] ?? [];
      acc[key].push(snap);
      return acc;
    },
    {},
  );

  const ensureSelection = (snap: PestSnapDTO): PestSelectionDTO => {
    const existing = selectedPestsRecord?.[snap.sourceId];
    if (existing) return existing;

    return {
      pest: snap,
      selected: false,
      locked: false,
      source: "MANUAL",
    };
  };

  const togglePest = (snap: PestSnapDTO) => {
    const current = ensureSelection(snap);
    if (requirement.immutable || current.locked) return;

    updateRequirement((prev) => ({
      selectedPests: {
        ...(prev.selectedPests ?? {}),
        [snap.sourceId]: {
          ...current,
          // Use snap as source of truth
          pest: snap,
          selected: !current.selected,
        },
      },
    }));
  };

  return (
    <div className="flex flex-col gap-2 border-b py-2">
      <H4>{t("Schädlinge")}</H4>

      <div className="flex flex-col gap-2">
        {Object.entries(pestsByKind).map(([kind, kindSnaps]) => (
          <div key={kind} className="flex flex-col gap-1">
            <div className="text-muted-foreground text-sm font-medium">
              {kind}
            </div>

            <div className="flex flex-col md:grid md:grid-cols-3 xl:grid-cols-5">
              {kindSnaps.map((snap) => {
                const selection = ensureSelection(snap);
                const disabled = requirement.immutable || selection.locked;

                const label =
                  snap.kind === PestKind.Indicator
                    ? getDisplayLabel(snap)
                    : snap.name;

                return (
                  <div
                    key={snap.sourceId}
                    className="flex items-center gap-1 p-1"
                  >
                    <Checkbox
                      checked={selection.selected}
                      onClick={() => togglePest(snap)}
                      disabled={disabled}
                    />
                    <Button
                      className="hover:text-primary bg-transparent p-1 text-base text-black hover:bg-transparent"
                      onClick={() => togglePest(snap)}
                      disabled={disabled}
                    >
                      {label}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
